/**
 * AuthContext — real `@auth0/auth0-react` wiring (Phase β wave 5).
 *
 * The public shape (`{ status, user, login, logout }`) is unchanged from
 * the wave-1 stub so consumers (`GuardedRoute`, `RootLayout`, pages) keep
 * working. The implementation now derives state from `useAuth0()`.
 *
 * Three modes:
 *   1. Test override — `initialStatus` / `initialUser` props bypass Auth0
 *      entirely. Router tests rely on this to simulate an authed session
 *      without booting the SDK.
 *   2. Dev fallback — if any of `VITE_AUTH0_DOMAIN`, `VITE_AUTH0_CLIENT_ID`,
 *      `VITE_AUTH0_AUDIENCE` is missing, we log a `console.warn` and run a
 *      stub provider (`anonymous`, no-op login/logout). Local dev is not
 *      blocked on Auth0 config.
 *   3. Live — wraps `Auth0Provider`, then exposes our context via an inner
 *      bridge component.
 *
 * Reference: docs/console-architecture.md §5.1 + §7.1.
 */

import { Suspense, createContext, lazy, useCallback, useContext, useMemo } from "react";
import type { ReactNode } from "react";

declare global {
  interface ImportMetaEnv {
    readonly VITE_AUTH0_DOMAIN?: string;
    readonly VITE_AUTH0_CLIENT_ID?: string;
    readonly VITE_AUTH0_AUDIENCE?: string;
  }
}

export type AuthStatus = "loading" | "anonymous" | "authenticated";

/**
 * Workspace slug claim key. Auth0 custom claims must be namespaced under
 * a URL — we read the workspace from this Action-injected claim.
 */
export const WORKSPACE_CLAIM = "https://animachord.dev/workspace_slug";

export interface AuthUser {
  readonly id: string;
  readonly email: string;
  readonly name: string;
  readonly workspaceSlug: string | null;
  readonly picture?: string;
}

export interface AuthContextValue {
  readonly status: AuthStatus;
  readonly user: AuthUser | null;
  readonly login: (returnTo?: string) => void;
  readonly logout: () => void;
}

const defaultValue: AuthContextValue = {
  status: "anonymous",
  user: null,
  login: () => {
    /* stub */
  },
  logout: () => {
    /* stub */
  },
};

export const AuthContext = createContext<AuthContextValue>(defaultValue);

const Auth0Live = lazy(() => import("./Auth0Live"));

/**
 * Surfaces that need a real answer from Auth0 whether or not this browser
 * has a session: the login and callback pages drive the SDK, and every
 * studio route is guarded and must not be told "anonymous" by a stub.
 */
const AUTH_REQUIRING_PATHS = ["/auth", "/studio"] as const;

/**
 * Should this page boot the Auth0 SDK at all?
 *
 * Splitting the SDK into its own chunk saves a visitor nothing on its
 * own, because `AuthProvider` mounts on every route and would simply
 * fetch it a moment later. This is the part that saves the 171 KB.
 *
 * Two ways to answer yes. The path needs auth, or this browser has
 * authenticated before -- `cacheLocation="localstorage"` means the SDK
 * leaves an `@@auth0spajs@@` key behind, so its presence is a reliable
 * "there may be a session to restore".
 *
 * A developer whose storage was cleared reads as anonymous on the public
 * pages until they open a studio route or sign in, at which point the SDK
 * boots and the marker returns. Self-healing, and the cost of being wrong
 * is a workspace name missing from the header of a page that does not use
 * it.
 *
 * Storage access throws in some privacy modes. Throwing here would take
 * the whole app down, so it is treated as "no session", which is both the
 * safe answer and usually the true one.
 */
function shouldBootAuth0(): boolean {
  if (typeof window === "undefined") return false;
  const path = window.location.pathname;
  if (AUTH_REQUIRING_PATHS.some((p) => path === p || path.startsWith(`${p}/`))) {
    return true;
  }
  try {
    for (let i = 0; i < window.localStorage.length; i += 1) {
      if (window.localStorage.key(i)?.startsWith("@@auth0spajs@@")) return true;
    }
  } catch {
    return false;
  }
  return false;
}

export interface AuthProviderProps {
  readonly children: ReactNode;
  /** Test-only overrides. Bypass Auth0 entirely when set. */
  readonly initialStatus?: AuthStatus;
  readonly initialUser?: AuthUser | null;
}

interface Auth0EnvConfig {
  readonly domain: string;
  readonly clientId: string;
  readonly audience: string;
}

function readAuth0Env(): Auth0EnvConfig | null {
  const env = import.meta.env as Record<string, string | undefined>;
  const domain = env["VITE_AUTH0_DOMAIN"];
  const clientId = env["VITE_AUTH0_CLIENT_ID"];
  const audience = env["VITE_AUTH0_AUDIENCE"];
  if (!domain || !clientId || !audience) return null;
  return { domain, clientId, audience };
}

/**
 * Stub provider used (a) when env vars are missing in dev, and (b) by tests
 * passing `initialStatus` / `initialUser`. No Auth0 SDK boot.
 */
function StubProvider({
  children,
  status,
  user,
}: {
  readonly children: ReactNode;
  readonly status: AuthStatus;
  readonly user: AuthUser | null;
}) {
  const noop = useCallback(() => {
    /* no-op in stub mode */
  }, []);
  const value = useMemo<AuthContextValue>(
    () => ({ status, user, login: noop, logout: noop }),
    [status, user, noop],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function AuthProvider({
  children,
  initialStatus,
  initialUser,
}: AuthProviderProps) {
  // Test override — never boots Auth0.
  if (initialStatus !== undefined || initialUser !== undefined) {
    return (
      <StubProvider status={initialStatus ?? "anonymous"} user={initialUser ?? null}>
        {children}
      </StubProvider>
    );
  }

  /*
    Nothing on this page will ask who you are, and nothing in this browser
    says you have ever signed in. Stay anonymous and never fetch the SDK.
  */
  if (!shouldBootAuth0()) {
    return (
      <StubProvider status="anonymous" user={null}>
        {children}
      </StubProvider>
    );
  }

  const env = readAuth0Env();
  if (env === null) {
    if (typeof console !== "undefined") {
      // eslint-disable-next-line no-console
      console.warn(
        "[auth] VITE_AUTH0_{DOMAIN,CLIENT_ID,AUDIENCE} not set — running stub provider. Login/logout are no-ops.",
      );
    }
    return (
      <StubProvider status="anonymous" user={null}>
        {children}
      </StubProvider>
    );
  }

  return (
    /*
      Children render immediately against the stub while the SDK chunk is
      in flight, rather than behind a blank Suspense boundary. A visitor
      who does reach here is anonymous until it resolves, which is what
      they were going to be anyway.
    */
    <Suspense
      fallback={
        <StubProvider status="loading" user={null}>
          {children}
        </StubProvider>
      }
    >
      <Auth0Live domain={env.domain} clientId={env.clientId} audience={env.audience}>
        {children}
      </Auth0Live>
    </Suspense>
  );
}

export function useAuth(): AuthContextValue {
  return useContext(AuthContext);
}
